import * as yup from 'yup';
import {
  MAXIMUM_STRING_LENGTH,
  MAXIMUM_TEXT_AREA_LENGTH,
} from '@common/types/constants.ts';
import { todayOrFutureDateSchema } from '@common/schema';

const requiredErrorMessage = 'This field is required';

export const addTaskSchema = yup.object().shape({
  patient_id: yup.string().required(requiredErrorMessage),
  provider_id: yup.string().when('category', {
    is: 'Care management',
    then: schema => schema.required(requiredErrorMessage),
    otherwise: schema => schema.optional(),
  }),
  title: yup.string().when('category', {
    is: 'Custom',
    then: schema =>
      schema
        .required(requiredErrorMessage)
        .max(
          MAXIMUM_STRING_LENGTH,
          `This input must be ${MAXIMUM_STRING_LENGTH} characters or less. Please reduce the length to proceed.`,
        ),
    otherwise: schema => schema.optional(),
  }),
  description: yup
    .string()
    .max(
      MAXIMUM_TEXT_AREA_LENGTH,
      `This input must be ${MAXIMUM_TEXT_AREA_LENGTH} characters or less. Please reduce the length to proceed.`,
    )
    .when('category', {
      is: 'Care management',
      then: schema => schema.required(requiredErrorMessage),
      otherwise: schema => schema.optional(),
    }),
  category: yup
    .string()
    .oneOf(['Custom', 'Pharmacy', 'Care management'])
    .required(requiredErrorMessage),
  expected_minutes: yup
    .number()
    // an emptied input arrives as '', which casts to NaN and would otherwise
    // fail the number type check even when the field is not required
    .transform(value => (Number.isNaN(value) ? undefined : value))
    .when(['category', 'order_type'], ([category, order_type], schema) => {
      if (category === 'Care management' && order_type === 'Completed task') {
        return schema
          .required(requiredErrorMessage)
          .typeError(requiredErrorMessage)
          .min(0, 'Minutes must be at least 0')
          .max(1440, 'Minutes cannot exceed 1440');
      }
      return schema.optional().nullable();
    }),
  urgency: yup.string().when('category', {
    is: 'Care management',
    then: schema =>
      schema
        .oneOf(['Stat (24h)', 'Urgent (48h)', 'Routine (72h)'])
        .required(requiredErrorMessage),
    otherwise: schema => schema.optional(),
  }),
  order_type: yup.string().when('category', {
    is: 'Care management',
    then: schema => schema.required(requiredErrorMessage),
    otherwise: schema => schema.optional(),
  }),
  order_set: yup.string().when('category', {
    is: 'Care management',
    then: schema => schema.required(requiredErrorMessage),
    otherwise: schema => schema.optional(),
  }),

  lab_requirements: yup
    .string()
    .when(['category', 'order_type'], ([category, order_type], schema) => {
      if (category === 'Care management' && order_type === 'Labs') {
        return schema.required(requiredErrorMessage);
      }
      return schema.optional();
    }),

  hospice_referral_origin: yup
    .string()
    .when(['category', 'order_type'], ([category, order_type], schema) => {
      if (category === 'Care management' && order_type === 'Hospice') {
        return schema.required(requiredErrorMessage);
      }
      return schema.optional();
    }),

  home_health_agency_name: yup
    .string()
    .when(['category', 'order_type'], ([category, order_type], schema) => {
      if (category === 'Care management' && order_type === 'Home Health') {
        return schema.required(requiredErrorMessage);
      }
      return schema.optional();
    }),
  home_health_provider_status: yup.string().optional(),

  external_hospice_agency_name: yup
    .string()
    .when(
      ['category', 'order_type', 'order_set'],
      ([category, order_type, order_set], schema) => {
        if (
          category === 'Care management' &&
          order_type === 'Hospice' &&
          order_set === 'External / Other'
        ) {
          return schema.required(requiredErrorMessage);
        }
        return schema.optional();
      },
    ),
  external_hospice_referral_reason: yup
    .string()
    .when(
      ['category', 'order_type', 'order_set'],
      ([category, order_type, order_set], schema) => {
        if (
          category === 'Care management' &&
          order_type === 'Hospice' &&
          order_set === 'External / Other'
        ) {
          return schema.required(requiredErrorMessage);
        }
        return schema.optional();
      },
    ),

  reason_for_external_referral: yup
    .string()
    .when(['category', 'order_set'], ([category, order_set], schema) => {
      if (category === 'Care management' && order_set === 'External Referral') {
        return schema.required(requiredErrorMessage);
      }
      return schema.optional();
    }),

  external_agency_name: yup
    .string()
    .when(['category', 'order_set'], ([category, order_set], schema) => {
      if (category === 'Care management' && order_set === 'External Referral') {
        return schema.required(requiredErrorMessage);
      }
      return schema.optional();
    }),

  note: yup
    .string()
    .max(
      MAXIMUM_TEXT_AREA_LENGTH,
      `This input must be ${MAXIMUM_TEXT_AREA_LENGTH} characters or less. Please reduce the length to proceed.`,
    )
    .when(['category', 'order_type'], ([category, order_type], schema) => {
      if (category === 'Care management' && order_type === 'Completed task') {
        return schema.required(requiredErrorMessage);
      }
      return schema.optional();
    }),

  note_type: yup
    .string()
    .when(['category', 'order_type'], ([category, order_type], schema) => {
      if (category === 'Care management' && order_type === 'Completed task') {
        return schema.required(requiredErrorMessage);
      }
      return schema.optional();
    }),

  note_program_id: yup.string().nullable().optional(),

  notable_call: yup.boolean().optional(),

  other_specifier: yup
    .string()
    .when(
      [
        'category',
        'order_set',
        'order_type',
        'home_health_agency_name',
        'external_hospice_agency_name',
        'hospice_referral_origin',
        'external_hospice_referral_reason',
        'reason_for_external_referral',
      ],
      (
        [
          category,
          order_set,
          order_type,
          home_health_agency_name,
          external_hospice_agency_name,
          hospice_referral_origin,
          external_hospice_referral_reason,
          reason_for_external_referral,
        ],
        schema,
      ) => {
        const mustRequire =
          category === 'Care management' &&
          (order_set === 'Other' ||
            (order_type === 'Home Health' &&
              home_health_agency_name === 'Other') ||
            (order_type === 'Palliative Care' &&
              order_set === 'External Referral' &&
              reason_for_external_referral === 'Other') ||
            (order_type === 'Hospice' &&
              (external_hospice_agency_name === 'Other' ||
                hospice_referral_origin === 'Other' ||
                (external_hospice_referral_reason === 'Other' &&
                  order_set === 'External / Other'))));

        return mustRequire
          ? schema
              .required(requiredErrorMessage)
              .max(
                MAXIMUM_STRING_LENGTH,
                `This input must be ${MAXIMUM_STRING_LENGTH} characters or less. Please reduce the length to proceed.`,
              )
          : schema
              .optional()
              .max(
                MAXIMUM_STRING_LENGTH,
                `This input must be ${MAXIMUM_STRING_LENGTH} characters or less. Please reduce the length to proceed.`,
              );
      },
    ),

  due_date: yup.string().when('category', {
    is: 'Care management',
    then: schema =>
      schema
        .optional()
        .nullable()
        .concat(todayOrFutureDateSchema.optional().nullable()),
    otherwise: schema =>
      schema.required(requiredErrorMessage).concat(todayOrFutureDateSchema),
  }),
  assign_to: yup
    .string()
    .oneOf(['user', 'team', 'self', 'assignedCm'])
    .required(requiredErrorMessage),
  user_id: yup
    .string()
    .when('assign_to', ([assign_to], schema) =>
      assign_to === 'user'
        ? schema.required(requiredErrorMessage)
        : schema.optional(),
    ),
  team_id: yup
    .string()
    .when('assign_to', ([assign_to], schema) =>
      assign_to === 'team'
        ? schema.required(requiredErrorMessage)
        : schema.optional(),
    ),
  recurrence: yup.string().when('category', {
    is: 'Custom',
    then: schema => schema.required(requiredErrorMessage),
    otherwise: schema => schema.optional(),
  }),
});
